import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  getGetNarrativeWalkthroughQueryKey,
  useGetNarrativeWalkthrough,
  usePatchNarrativeWalkthroughStep,
} from '@/api/generated/narrative/narrative';

export type WalkthroughPhase = 'intro' | 'upload_prompt' | 'wrap_up' | 'done';

interface UseNarrativeWalkthroughOptions {
  onError?: (error: Error) => void;
}

/**
 * Single source of truth for the Narrative walkthrough state machine.
 * Derives `phase` from the server's (walkThroughStep, hasSeenNarrativeIntro)
 * pair plus a transient client-only `wrap_up` flag for the post-skip
 * acknowledgment screen.
 */
export function useNarrativeWalkthrough({ onError }: UseNarrativeWalkthroughOptions = {}) {
  const queryClient = useQueryClient();
  const [wrapUpPending, setWrapUpPending] = useState(false);

  const statusQuery = useGetNarrativeWalkthrough({
    query: {
      gcTime: 1000 * 60 * 5,
      staleTime: 0,
    },
  });

  const stepMutation = usePatchNarrativeWalkthroughStep({
    mutation: {
      onError: (error) => {
        onError?.(error instanceof Error ? error : new Error(String(error)));
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetNarrativeWalkthroughQueryKey() });
      },
      retry: false,
    },
  });

  const status = statusQuery.data;
  const step = status?.walkThroughStep ?? 1;
  const introSeen = status?.hasSeenNarrativeIntro ?? false;

  let phase: WalkthroughPhase;
  if (wrapUpPending) {
    phase = 'wrap_up';
  } else if (introSeen) {
    phase = 'done';
  } else if (step === 1) {
    phase = 'intro';
  } else if (step === 2) {
    phase = 'upload_prompt';
  } else {
    // Server says step=3 but intro-seen=false. With the target-step PATCH this
    // shouldn't occur, but show wrap_up defensively rather than skipping the
    // acknowledgment screen entirely.
    phase = 'wrap_up';
  }

  const setStep = (target: 1 | 2 | 3) => {
    stepMutation.mutate({ data: { step: target } });
  };

  /** Move from `intro` to `upload_prompt`. */
  const advance = () => setStep(2);

  /** Skip the upload prompt; show `wrap_up` and mark the user completed server-side. */
  const skip = () => {
    setWrapUpPending(true);
    setStep(3);
  };

  /** Called after a successful upload to mark the walkthrough completed. */
  const markUploaded = () => setStep(3);

  /** Dismiss the `wrap_up` screen; phase resolves to `done`. */
  const acknowledge = () => setWrapUpPending(false);

  return {
    acknowledge,
    advance,
    isLoading: statusQuery.isLoading,
    markUploaded,
    phase,
    skip,
  };
}
